import { Host, forceUpdate, h } from "@stencil/core";
import { removeElement } from "../../common/array";
import { SCROLLABLE_CLASS } from "../../common/reserved-names";
import { adoptCommonThemes } from "../../common/theme";
import { mouseEventModifierKey } from "../common/helpers";
import { flattenActionListUIModel } from "./flatten-model";
import { actionListKeyboardNavigation } from "./keyboard-navigation";
import { selectedItemsChangeShouldBeEmitted, setActionListSelectedItems } from "./selections";
import { actionListDefaultTranslations } from "./translations";
import { updateItemProperty } from "./update-item-property";
import { ACTION_LIST_ITEM_TAG, getActionListItemOrGroupInfo, getActionListOrGroupItemFromEvent, getParentArray } from "./utils";
const DEFAULT_EDITABLE_ITEMS_VALUE = true;
// const DEFAULT_ORDER_VALUE = 0;
// const defaultGetImagePath: ActionListImagePathCallback = (imgSrc: string) =>
//   imgSrc;
const renderMapping = {
    actionable: (itemModel, actionListRenderState, disabled, nested = false, nestedExpandable = false) => (h("ch-action-list-item", { key: itemModel.id, id: itemModel.id, additionalInfo: itemModel.additionalInformation, caption: itemModel.caption, checkbox: itemModel.checkbox ?? actionListRenderState.checkbox, checked: itemModel.checked ?? actionListRenderState.checked, disabled: disabled === true
            ? true
            : itemModel.disabled ?? actionListRenderState.disabled, editable: itemModel.editable ?? actionListRenderState.editableItems, fixed: itemModel.fixed, getImagePathCallback: actionListRenderState.getImagePathCallback, metadata: itemModel.metadata, nested: nested, nestedExpandable: nestedExpandable, selectable: actionListRenderState.selection !== "none", selected: itemModel.selected, translations: actionListRenderState.translations })),
    group: (itemModel, actionListRenderState) => (h("ch-action-list-group", { key: itemModel.id, id: itemModel.id, caption: itemModel.caption, disabled: itemModel.disabled ?? actionListRenderState.disabled, expandable: itemModel.expandable, expanded: itemModel.expanded, selected: itemModel.selected }, itemModel.items?.map(item => actionListRenderState.renderItem(item, actionListRenderState, itemModel.disabled, true, itemModel.expandable)))),
    separator: () => (h("div", { role: "separator", "aria-hidden": "true", class: "separator", part: "separator" }))
};
const defaultRenderItem = (itemModel, actionListRenderState, disabled, nested, nestedExpandable) => itemModel.type === "actionable"
    ? renderMapping.actionable(itemModel, // THIS IS A WA
    actionListRenderState, disabled, nested, nestedExpandable)
    : renderMapping[itemModel.type](itemModel, // THIS IS A WA
    actionListRenderState);
const FIRST_ITEM_GREATER_THAN_SECOND = -1;
const SECOND_ITEM_GREATER_THAN_FIRST = 0;
const defaultSortItemsCallback = (subModel) => {
    subModel.sort((a, b) => {
        // Rules:
        //   - Checks fixed value.
        //   - If can't decide, checks "order" value.
        //   - If can't decide, checks "caption" value.
        // Both, "a" and "b" are fixed
        if (a.type === "actionable" && b.type === "actionable") {
            if (a.fixed && !b.fixed) {
                return FIRST_ITEM_GREATER_THAN_SECOND;
            }
            if (!a.fixed && b.fixed) {
                return SECOND_ITEM_GREATER_THAN_FIRST;
            }
        }
        // Only "a" is fixed
        else if (a.type === "actionable" && a.fixed) {
            return FIRST_ITEM_GREATER_THAN_SECOND;
        }
        // Only "b" is fixed
        else if (b.type === "actionable" && b.fixed) {
            return SECOND_ITEM_GREATER_THAN_FIRST;
        }
        if (a.order < b.order) {
            return FIRST_ITEM_GREATER_THAN_SECOND;
        }
        if (a.order > b.order) {
            return SECOND_ITEM_GREATER_THAN_FIRST;
        }
        return a.type === "actionable" &&
            b.type === "actionable" &&
            a.caption <= b.caption
            ? FIRST_ITEM_GREATER_THAN_SECOND
            : SECOND_ITEM_GREATER_THAN_FIRST;
    });
};
export class ChActionListRender {
    constructor() {
        this.expanded = true;
        this.checkbox = false;
        this.checked = false;
        this.disabled = false;
        this.editableItems = DEFAULT_EDITABLE_ITEMS_VALUE;
        this.fixItemCallback = undefined;
        this.getImagePathCallback = undefined;
        this.model = [];
        this.modifyItemCaptionCallback = undefined;
        this.renderItem = defaultRenderItem;
        this.removeItemCallback = undefined;
        this.selection = "none";
        this.sortItemsCallback = defaultSortItemsCallback;
        this.translations = actionListDefaultTranslations;
    }
    #flattenedModel = new Map();
    // #additionalItemsParts: Set<string> | undefined;
    #selectedItems = undefined;
    #shouldUpdateModelAndSelection = false;
    el;
    modelChanged() {
        this.#shouldUpdateModelAndSelection = true;
    }
    selectionChanged() {
        this.#shouldUpdateModelAndSelection = true;
    }
    /**
     * Fired when the selected items change and `selection !== "none"`
     */
    selectedItemsChange;
    /**
     * Fired when an item is clicked and `selection === "none"`.
     * Applies for items that have `type === "actionable"` or
     * (`type === "group"` and `expandable === true`)
     */
    itemClick;
    /**
     * Adds an item in the control.
     *
     * If the item already exists, the operation is canceled.
     *
     * If the `groupParentId` property is specified the item is added in the
     * group determined by `groupParentId`. It only works if the item to add
     * has `type === "actionable"`
     */
    async addItem(itemInfo, groupParentId) {
        // Already exists
        if (this.#flattenedModel.get(itemInfo.id)) {
            return;
        }
        if (groupParentId) {
            const parentGroup = this.#flattenedModel.get(groupParentId);
            // The parent group does not exists or it isn't a group
            if (!parentGroup ||
                parentGroup.item.type !== "group" ||
                itemInfo.type !== "actionable") {
                return;
            }
            parentGroup.item.items.push(itemInfo);
            this.#flattenedModel.set(itemInfo.id, {
                item: itemInfo,
                parentItem: parentGroup.item
            });
            // Sort items in parent model
            this.#sortModel(parentGroup.item.items);
        }
        // Item is placed at the root
        else {
            this.model.push(itemInfo);
            this.#flattenedModel.set(itemInfo.id, {
                item: itemInfo,
                root: this.model
            });
            // Sort items in parent model
            this.#sortModel(this.model);
        }
        forceUpdate(this);
    }
    /**
     * Given a list of ids, it returns an array of the items that exists in the
     * given list.
     */
    async getItemsInfo(itemsId) {
        return this.#getItemsInfo(itemsId);
    }
    /**
     * Remove the item and all its descendants from the control.
     */
    async removeItem(itemId) {
        const itemUIModel = this.#flattenedModel.get(itemId);
        if (!itemUIModel) {
            return;
        }
        // Remove all descendants
        if (itemUIModel.item.type === "group") {
            const items = itemUIModel.item.items;
            items.forEach(item => {
                this.#flattenedModel.delete(item.id);
            });
        }
        this.#removeItem(itemUIModel);
    }
    #getItemsInfo = (itemsId) => {
        const actionListItemsInfo = [];
        itemsId.forEach(itemId => {
            const itemUIModel = this.#flattenedModel.get(itemId);
            if (itemUIModel) {
                actionListItemsInfo.push(itemUIModel);
            }
        });
        return actionListItemsInfo;
    };
    /**
     * Given an itemId and the properties to update, it updates the properties
     * of the items in the list.
     */
    async updateItemProperties(itemId, properties) {
        // Set to check if there are new selected items
        const newSelectedItems = new Set(this.#selectedItems);
        const parentArray = updateItemProperty(itemId, properties, this.#flattenedModel, newSelectedItems);
        if (parentArray !== undefined) {
            this.#sortModel(parentArray);
        }
        if (selectedItemsChangeShouldBeEmitted(this.#selectedItems, newSelectedItems, this.#flattenedModel, this.selection)) {
            this.#selectedItems = newSelectedItems;
            this.#emitSelectedItemsChange();
        }
        forceUpdate(this);
    }
    onCaptionChange(event) {
        if (!this.modifyItemCaptionCallback) {
            return;
        }
        event.stopPropagation();
        const itemRef = event
            .composedPath()
            .find(el => el.tagName &&
            el.tagName?.toLowerCase() === ACTION_LIST_ITEM_TAG);
        if (!itemRef) {
            return;
        }
        const itemId = event.detail.itemId;
        const itemUIModel = this.#flattenedModel.get(itemId);
        const itemInfo = itemUIModel.item;
        const newCaption = event.detail.newCaption;
        const oldCaption = itemInfo.caption;
        // Optimistic UI: Update the caption in the UI Model before the change is
        // completed in the server
        itemInfo.caption = newCaption;
        itemRef.caption = newCaption;
        this.modifyItemCaptionCallback(itemId, newCaption)
            .then(() => {
            // Sort items in parent model
            this.#sortModel(getParentArray(itemUIModel));
            // Update filters
            // this.#scheduleFilterProcessing();
            // Force re-render
            forceUpdate(this);
        })
            .catch(() => {
            // TODO: Should we do something with the error message?
            itemRef.caption = oldCaption;
            itemInfo.caption = oldCaption;
        });
    }
    onFixedChange(event) {
        const detail = event.detail;
        const itemUIModel = this.#flattenedModel.get(detail.itemId);
        const itemInfo = itemUIModel.item;
        if (!this.fixItemCallback) {
            this.#updateItemFix(itemUIModel, itemInfo, detail.value);
            return;
        }
        this.fixItemCallback(itemInfo, detail.value).then(acceptChange => {
            if (acceptChange) {
                this.#updateItemFix(itemUIModel, itemInfo, detail.value);
            }
        });
    }
    #updateItemFix = (itemUIModel, itemInfo, newFixedValue) => {
        itemInfo.fixed = newFixedValue;
        // Sort items in parent model
        this.#sortModel(getParentArray(itemUIModel));
        // Queue a re-render to update the fixed binding and the order of the items
        forceUpdate(this);
    };
    onRemove(event) {
        const itemUIModel = this.#flattenedModel.get(event.detail);
        if (!this.removeItemCallback) {
            this.#removeItem(itemUIModel);
            return;
        }
        this.removeItemCallback(itemUIModel.item).then(acceptRemove => {
            if (acceptRemove) {
                this.#removeItem(itemUIModel);
            }
        });
    }
    #getItemOrGroupInfo = (itemId) => getActionListItemOrGroupInfo(itemId, this.#flattenedModel);
    #updateAndEmitSelectedItems = (selection) => {
        if (selection === "none") {
            this.#removeAllSelectedItems();
            this.#selectedItems = undefined;
        }
        // Create the set to allocate the selected items, if necessary
        else {
            // First render. Do not check to emit selectedItemsChange event.
            if (!this.#selectedItems) {
                this.#selectedItems = new Set();
                // TODO: Add a unit test for "?? []"
                setActionListSelectedItems(this.model ?? [], this.#selectedItems);
                return;
            }
            const newSelectedItems = new Set();
            setActionListSelectedItems(this.model ?? [], newSelectedItems);
            if (selectedItemsChangeShouldBeEmitted(this.#selectedItems, newSelectedItems, this.#flattenedModel, this.selection)) {
                this.#selectedItems = newSelectedItems;
                forceUpdate(this);
                this.#emitSelectedItemsChange();
            }
        }
    };
    #removeAllSelectedItems = () => {
        if (this.#selectedItems) {
            this.#selectedItems.forEach(selectedItemId => {
                const selectedItemInfo = this.#getItemOrGroupInfo(selectedItemId);
                selectedItemInfo.selected = false;
            });
            this.#selectedItems.clear();
        }
    };
    #handleItemClick = (event) => {
        const actionListItemOrGroup = getActionListOrGroupItemFromEvent(event);
        if (!actionListItemOrGroup ||
            actionListItemOrGroup.editing) {
            return;
        }
        const itemInfo = this.#getItemOrGroupInfo(actionListItemOrGroup.id);
        this.#checkIfMustExpandCollapseGroup(itemInfo);
        if (itemInfo.type === "group" && !itemInfo.expandable) {
            return;
        }
        this.itemClick.emit(this.#flattenedModel.get(itemInfo.id));
    };
    #checkIfMustExpandCollapseGroup = (itemInfo) => {
        // Toggle the expanded/collapsed in the group on click
        if (itemInfo.type === "group" &&
            itemInfo.expandable &&
            !itemInfo.disabled) {
            itemInfo.expanded = !itemInfo.expanded;
            forceUpdate(this);
        }
    };
    #handleItemSelection = (event) => {
        const actionListItemOrGroup = getActionListOrGroupItemFromEvent(event);
        if (!actionListItemOrGroup ||
            actionListItemOrGroup.editing) {
            return;
        }
        const itemId = actionListItemOrGroup.id;
        const itemInfo = this.#getItemOrGroupInfo(itemId);
        const ctrlKeyIsPressed = mouseEventModifierKey(event);
        const itemWasSelected = this.#selectedItems.has(itemId);
        const singleSelectionMode = this.selection === "single";
        // - - - - - - - - - - Single selection - - - - - - - - - -
        if (singleSelectionMode) {
            if (!ctrlKeyIsPressed) {
                this.#checkIfMustExpandCollapseGroup(itemInfo);
            }
            // Nothing to update in the UI
            if (itemWasSelected && !ctrlKeyIsPressed) {
                return;
            }
            const previousSelectedItemId = [
                ...this.#selectedItems.keys()
            ][0];
            // Remove the previous selected item
            if (previousSelectedItemId) {
                this.#selectedItems.clear();
                const previousSelectedItemInfo = this.#getItemOrGroupInfo(previousSelectedItemId);
                previousSelectedItemInfo.selected = false;
            }
            // If the item was not selected, add it to the Set. If the item was
            // selected, the previous if removes the item
            if (!itemWasSelected) {
                this.#selectedItems.add(itemId);
            }
            itemInfo.selected = !itemWasSelected;
            this.#emitSelectedItemsChange();
            forceUpdate(this);
            return;
        }
        // - - - - - - - - - - Multiple selection - - - - - - - - - -
        if (ctrlKeyIsPressed) {
            // The item was selected, deselect the item
            if (itemWasSelected) {
                this.#selectedItems.delete(itemId);
            }
            // Otherwise, select the item
            else {
                this.#selectedItems.add(itemId);
            }
            itemInfo.selected = !itemWasSelected;
        }
        else {
            this.#checkIfMustExpandCollapseGroup(itemInfo);
            // Remove the selection from all items
            this.#removeAllSelectedItems();
            this.#selectedItems.add(itemId);
            itemInfo.selected = true;
        }
        this.#emitSelectedItemsChange();
        forceUpdate(this);
    };
    #emitSelectedItemsChange = () => {
        const selectedItemsInfo = this.#getItemsInfo([
            ...this.#selectedItems.keys()
        ]);
        this.selectedItemsChange.emit(selectedItemsInfo);
    };
    #removeItem = (itemUIModel) => {
        const parentArray = itemUIModel.root ??
            itemUIModel.parentItem.items;
        const itemToRemoveId = itemUIModel.item.id;
        const itemToRemoveIndex = parentArray.findIndex(el => el.id === itemToRemoveId);
        // In some situations, the user could remove the item before the
        // "removeItemCallback" promise is resolved
        if (itemToRemoveIndex > -1) {
            // Remove the UI model from the previous parent. The equality function
            // must be by index, not by object reference
            removeElement(parentArray, itemToRemoveIndex);
        }
        this.#flattenedModel.delete(itemToRemoveId);
        // Queue a re-render
        forceUpdate(this);
    };
    #sortModel = (model) => {
        if (this.sortItemsCallback) {
            this.sortItemsCallback(model);
        }
    };
    #flattenUIModel = (model) => flattenActionListUIModel(model, this.#flattenedModel, this.#sortModel);
    // #processAdditionalItemParts = () => {
    //   this.#additionalItemsParts = undefined;
    //   this.#additionalItemsParts ??= new Set();
    // }
    connectedCallback() {
        adoptCommonThemes(this.el.shadowRoot.adoptedStyleSheets);
        this.#flattenUIModel(this.model);
        this.#updateAndEmitSelectedItems(this.selection);
        this.el.setAttribute("role", "list");
    }
    // Don't turn it into the componentWillUpdate lifecycle method.
    // For some reason, the componentWillUpdate lifecycle method is not
    // dispatched when an Angular page (with SSR) is served from the server, but
    // when navigating in the SPA, the componentWillUpdate method works
    componentWillRender() {
        if (this.#shouldUpdateModelAndSelection) {
            this.#shouldUpdateModelAndSelection = false;
            this.#flattenUIModel(this.model);
            this.#updateAndEmitSelectedItems(this.selection);
        }
    }
    render() {
        return (h(Host, { key: 'ab4f7f09fe99016c3b205e5ffe5873a1a07c63f5', "aria-multiselectable": this.selection === "multiple" ? "true" : null, class: SCROLLABLE_CLASS, onClick: this.selection === "none"
                ? this.#handleItemClick
                : this.#handleItemSelection, onKeyDown: actionListKeyboardNavigation(this.el, this.#flattenedModel) }, this.model?.map(item => this.renderItem(item, this, false, false, false))));
    }
    static get is() { return "ch-action-list-render"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["action-list-render.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["action-list-render.css"]
        };
    }
    static get properties() {
        return {
            "checkbox": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want display a checkbox in all items by default."
                },
                "attribute": "checkbox",
                "reflect": false,
                "defaultValue": "false"
            },
            "checked": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want the checkbox to be checked in all items by\ndefault.\nOnly works if `checkbox = true`"
                },
                "attribute": "checked",
                "reflect": false,
                "defaultValue": "false"
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if all items are disabled.\nIf disabled, action list items will not fire any user interaction related\nevent (for example, `selectedItemsChange` event)."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "editableItems": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the edit operation is enabled in all\nitems by default. If `true`, the items can edit its caption in place."
                },
                "attribute": "editable-items",
                "reflect": false,
                "defaultValue": "DEFAULT_EDITABLE_ITEMS_VALUE"
            },
            "fixItemCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    itemInfo: ActionListItemActionable,\n    newFixedValue: boolean\n  ) => Promise<boolean>",
                    "resolved": "(itemInfo: ActionListItemActionable, newFixedValue: boolean) => Promise<boolean>",
                    "references": {
                        "ActionListItemActionable": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-list/types.ts::ActionListItemActionable"
                        },
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed when and item requests to be fixed/unfixed.\nIf the callback is not defined, the item will be fixed/unfixed without\nfurther confirmation."
                }
            },
            "getImagePathCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ActionListImagePathCallback",
                    "resolved": "(additionalItem: ActionListItemAdditionalBase) => GxImageMultiState",
                    "references": {
                        "ActionListImagePathCallback": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-list/types.ts::ActionListImagePathCallback"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies a callback that is executed when the path for an\nimgSrc needs to be resolved."
                }
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ActionListModel",
                    "resolved": "ActionListItemModel[]",
                    "references": {
                        "ActionListModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-list/types.ts::ActionListModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you define the model of the control."
                },
                "defaultValue": "[]"
            },
            "modifyItemCaptionCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    actionListItemId: string,\n    newCaption: string\n  ) => Promise<void>",
                    "resolved": "(actionListItemId: string, newCaption: string) => Promise<void>",
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed when a item request to modify its caption."
                }
            },
            "renderItem": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    itemModel: ActionListItemModel,\n    actionListRenderState: ChActionListRender,\n    disabled?: boolean,\n    nested?: boolean,\n    nestedExpandable?: boolean\n  ) => any",
                    "resolved": "(itemModel: ActionListItemModel, actionListRenderState: ChActionListRender, disabled?: boolean, nested?: boolean, nestedExpandable?: boolean) => any",
                    "references": {
                        "ActionListItemModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-list/types.ts::ActionListItemModel"
                        },
                        "ChActionListRender": {
                            "location": "global",
                            "id": "global::ChActionListRender"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property allows us to implement custom rendering of action-list items."
                },
                "defaultValue": "defaultRenderItem"
            },
            "removeItemCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    itemInfo: ActionListItemActionable\n  ) => Promise<boolean>",
                    "resolved": "(itemInfo: ActionListItemActionable) => Promise<boolean>",
                    "references": {
                        "ActionListItemActionable": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-list/types.ts::ActionListItemActionable"
                        },
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed when and item requests to be removed.\nIf the callback is not defined, the item will be removed without further\nconfirmation."
                }
            },
            "selection": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"single\" | \"multiple\" | \"none\"",
                    "resolved": "\"multiple\" | \"none\" | \"single\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the type of selection implemented by the control."
                },
                "attribute": "selection",
                "reflect": false,
                "defaultValue": "\"none\""
            },
            "sortItemsCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(subModel: ActionListModel) => void",
                    "resolved": "(subModel: ActionListModel) => void",
                    "references": {
                        "ActionListModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-list/types.ts::ActionListModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed when the action-list model is changed to order its items."
                },
                "defaultValue": "defaultSortItemsCallback"
            },
            "translations": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ActionListTranslations",
                    "resolved": "{ confirmDelete: string; cancelDelete: string; confirmModify: string; cancelModify: string; }",
                    "references": {
                        "ActionListTranslations": {
                            "location": "import",
                            "path": "../../components",
                            "id": "src/components.d.ts::ActionListTranslations"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the literals required for the control."
                },
                "defaultValue": "actionListDefaultTranslations"
            }
        };
    }
    static get states() {
        return {
            "expanded": {}
        };
    }
    static get events() {
        return [{
                "method": "selectedItemsChange",
                "name": "selectedItemsChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the selected items change and `selection !== \"none\"`"
                },
                "complexType": {
                    "original": "ActionListItemModelExtended[]",
                    "resolved": "ActionListItemModelExtended[]",
                    "references": {
                        "ActionListItemModelExtended": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-list/types.ts::ActionListItemModelExtended"
                        }
                    }
                }
            }, {
                "method": "itemClick",
                "name": "itemClick",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when an item is clicked and `selection === \"none\"`.\nApplies for items that have `type === \"actionable\"` or\n(`type === \"group\"` and `expandable === true`)"
                },
                "complexType": {
                    "original": "ActionListItemModelExtended",
                    "resolved": "{ parentItem: ActionListItemGroup; item: ActionListItemModel; } | { root: ActionListModel; item: ActionListItemModel; }",
                    "references": {
                        "ActionListItemModelExtended": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-list/types.ts::ActionListItemModelExtended"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "addItem": {
                "complexType": {
                    "signature": "(itemInfo: ActionListItemModel, groupParentId?: string) => Promise<void>",
                    "parameters": [{
                            "name": "itemInfo",
                            "type": "ActionListItemActionable | ActionListItemGroup | ActionListItemSeparator",
                            "docs": ""
                        }, {
                            "name": "groupParentId",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "ActionListItemModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-list/types.ts::ActionListItemModel"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Adds an item in the control.\n\nIf the item already exists, the operation is canceled.\n\nIf the `groupParentId` property is specified the item is added in the\ngroup determined by `groupParentId`. It only works if the item to add\nhas `type === \"actionable\"`",
                    "tags": []
                }
            },
            "getItemsInfo": {
                "complexType": {
                    "signature": "(itemsId: string[]) => Promise<ActionListItemModelExtended[]>",
                    "parameters": [{
                            "name": "itemsId",
                            "type": "string[]",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "ActionListItemModelExtended": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-list/types.ts::ActionListItemModelExtended"
                        }
                    },
                    "return": "Promise<ActionListItemModelExtended[]>"
                },
                "docs": {
                    "text": "Given a list of ids, it returns an array of the items that exists in the\ngiven list.",
                    "tags": []
                }
            },
            "removeItem": {
                "complexType": {
                    "signature": "(itemId: string) => Promise<void>",
                    "parameters": [{
                            "name": "itemId",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Remove the item and all its descendants from the control.",
                    "tags": []
                }
            },
            "updateItemProperties": {
                "complexType": {
                    "signature": "(itemId: string, properties: Partial<ActionListItemModel> & { type: ActionListItemType; }) => Promise<void>",
                    "parameters": [{
                            "name": "itemId",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "properties",
                            "type": "Partial<ActionListItemModel> & { type: ActionListItemType; }",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "Partial": {
                            "location": "global",
                            "id": "global::Partial"
                        },
                        "ActionListItemModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-list/types.ts::ActionListItemModel"
                        },
                        "ActionListItemType": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-list/types.ts::ActionListItemType"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given an itemId and the properties to update, it updates the properties\nof the items in the list.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "model",
                "methodName": "modelChanged"
            }, {
                "propName": "selection",
                "methodName": "selectionChanged"
            }];
    }
    static get listeners() {
        return [{
                "name": "captionChange",
                "method": "onCaptionChange",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "fixedChange",
                "method": "onFixedChange",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "remove",
                "method": "onRemove",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
